import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, createFileRoute } from '@tanstack/react-router'
import { PrinterIcon } from 'lucide-react'
import { useState } from 'react'

import { LoadingPage, LoadingRows } from '@/components/skeletons'
import { Page } from '@/components/page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CabinetScreen, type CabinetSearch } from '@/features/cabinets/CabinetScreen'
import { ShowInCabinet } from '@/features/cabinets/ShowInCabinet'
import { PrintLabelDialog } from '@/features/labels/PrintDialog'
import { LotActions } from '@/features/stock/LotActions'
import { fetchLocationContents, invalidateStock, locationContentsKey } from '@/features/stock/api'
import { atTheShelf } from '@/features/shelf/posture'
import { TREE_API } from '@/features/trees/api'
import { formatAmount } from '@/lib/quantity'

function parseSearch(search: Record<string, unknown>): CabinetSearch {
  const text = (key: string) =>
    typeof search[key] === 'string' && search[key] ? (search[key] as string) : undefined
  return {
    q: text('q'),
    drawer: text('drawer'),
    tint: search.tint === 'footprint' ? ('footprint' as const) : undefined,
  }
}

export const Route = createFileRoute('/locations/$locationId')({
  validateSearch: parseSearch,
  component: LocationPage,
})

/** A cabinet gets its grid; any other location, the list of what is in it. */
function LocationPage() {
  const { locationId } = Route.useParams()
  const search = Route.useSearch()

  const location = useQuery({
    queryKey: ['tree', 'locations', locationId],
    queryFn: () => TREE_API.locations.get(locationId),
  })

  if (location.isPending) {
    return (
      <Page title="Storage location">
        <LoadingPage label="Loading the location" />
      </Page>
    )
  }
  // A scanned drawer label on a phone at the shelf opens the shelf's drawer (D38).
  if (location.data?.slot_row != null && atTheShelf()) {
    return <Navigate to="/shelf/drawer/$locationId" params={{ locationId }} replace />
  }
  if (location.data?.grid_rows != null) {
    return <CabinetScreen cabinetId={locationId} search={search} />
  }
  return (
    <ContentsPage
      locationId={locationId}
      detail={location.data as Record<string, unknown> | undefined}
    />
  )
}

function ContentsPage({
  locationId,
  detail,
}: {
  locationId: string
  detail: Record<string, unknown> | undefined
}) {
  const queryClient = useQueryClient()
  const [includeChildren, setIncludeChildren] = useState(true)
  const [printing, setPrinting] = useState(false)

  const contents = useQuery({
    queryKey: locationContentsKey(locationId, includeChildren),
    queryFn: () => fetchLocationContents(locationId, includeChildren),
  })

  const refresh = () => invalidateStock(queryClient)

  const path = (detail?.path as { id: string; name: string }[] | undefined) ?? []
  const rows = contents.data ?? []

  return (
    <Page
      title={(detail?.name as string) ?? 'Storage location'}
      description={path.map((segment) => segment.name).join(' / ')}
      actions={
        <>
          {detail?.short_code ? (
            <Badge variant="outline" className="tabular tracking-widest">
              {String(detail.short_code)}
            </Badge>
          ) : null}
          <Button variant="outline" onClick={() => setPrinting(true)}>
            <PrinterIcon />
            Print label
          </Button>
        </>
      }
    >
      <PrintLabelDialog
        open={printing}
        onOpenChange={setPrinting}
        targets={[{ kind: 'storage_location', id: locationId }]}
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={includeChildren}
              onCheckedChange={(checked) => setIncludeChildren(checked === true)}
            />
            Include everything stored inside
          </label>
          {detail?.is_full === true && <Badge variant="warning">Full</Badge>}
          {detail?.only_single_part === true && <Badge variant="muted">One part only</Badge>}
          {detail?.limit_to_existing_parts === true && (
            <Badge variant="muted">Only parts already here</Badge>
          )}
        </div>

        {contents.isPending ? (
          <LoadingRows label="Loading the contents" rows={4} />
        ) : contents.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the contents</AlertTitle>
            <AlertDescription>
              {contents.error instanceof Error ? contents.error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground rounded-lg border border-dashed p-10 text-center text-sm">
            Nothing is stored here.
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Part</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Where exactly</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link
                        to="/parts/$partId"
                        params={{ partId: row.part_id }}
                        className="hover:text-primary font-medium"
                      >
                        {row.part_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.description || '—'}
                      {row.needs_refill && (
                        <Badge variant="warning" className="ml-2">
                          refill
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.location_path.map((segment) => segment.name).join(' / ') || '—'}
                      {row.cabinet_id && (
                        <ShowInCabinet cabinetId={row.cabinet_id} drawerId={row.location_id} />
                      )}
                    </TableCell>
                    <TableCell className="tabular text-right">
                      {row.amount_unknown ? (
                        <span className="text-muted-foreground">uncounted</span>
                      ) : (
                        formatAmount(row.amount, row.unit_symbol)
                      )}
                    </TableCell>
                    <TableCell>
                      <LotActions
                        label={row.part_name}
                        lot={{
                          id: row.id,
                          amount: row.amount,
                          amount_unknown: row.amount_unknown,
                          location_id: row.location_id ?? null,
                        }}
                        unitSymbol={row.unit_symbol}
                        // The list does not carry the part's unit rule, so the
                        // client does not pre-empt it; the backend still refuses
                        // a fractional amount on a whole-unit part.
                        wholeUnitsOnly={false}
                        onDone={refresh}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Page>
  )
}
